import React, { useState } from 'react';
import { Feature, Component, TestCase } from '../../types';
import { theme } from '../../theme';
import { Icons } from '../../utils/icons';

interface FeatureCardProps {
  feature: Feature;
  components: Component[];
  testCases: TestCase[];
  onToggleExpand: (featureId: string) => void;
  onDelete: (featureId: string) => Promise<void>;
  onEdit?: (feature: Feature) => void;
}

const FeatureCard: React.FC<FeatureCardProps> = ({
  feature,
  components,
  testCases,
  onToggleExpand,
  onDelete,
  onEdit
}) => {
  const featureComponents = components.filter(c => c.featureId === feature.id);
  
  const getTestCasesForComponent = (componentId: string): TestCase[] => {
    return testCases.filter(tc => tc.componentId === componentId);
  };

  // Get test cases at feature level (no component)
  const getTestCasesForFeature = (): TestCase[] => {
    return testCases.filter(tc => !tc.componentId && tc.featureId === feature.id);
  };

  // Count total test cases for this feature (component-level + feature-level)
  const getTotalTestCasesCount = (): number => {
    const componentTestCases = featureComponents.reduce((sum, comp) => {
      return sum + getTestCasesForComponent(comp.id).length;
    }, 0);
    const featureLevelTestCases = getTestCasesForFeature().length;
    return componentTestCases + featureLevelTestCases;
  };

  const [isHovered, setIsHovered] = useState(false);

  return (
    <div 
      className="card"
      onClick={() => onToggleExpand(feature.id)}
      style={{ 
        cursor: 'pointer', 
        transition: 'all 0.2s',
        padding: '1rem',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '180px'
      }}
      onMouseEnter={(e) => {
        setIsHovered(true);
        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        setIsHovered(false);
        e.currentTarget.style.boxShadow = '';
        e.currentTarget.style.transform = '';
      }}
    >
      {/* Title */}
      <div style={{ fontSize: '1rem', fontWeight: '600', color: theme.textPrimary, marginBottom: '0.5rem', lineHeight: '1.3' }}>
        {feature.name}
      </div>

      {/* Stats */}
      <div className="flex gap-xs items-center mb-xs" style={{ marginBottom: '0.5rem' }}>
        <span className="subdep-count-badge" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}>
          {featureComponents.length} component{featureComponents.length !== 1 ? 's' : ''}
        </span>
        {getTotalTestCasesCount() > 0 && (
          <span className="subdep-count-badge" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}>
            {getTotalTestCasesCount()} test case{getTotalTestCasesCount() !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Description */}
      {feature.description && (
        <p className="feature-description" style={{ 
          margin: 0, 
          marginBottom: '0.75rem',
          fontSize: '0.875rem', 
          color: theme.textSecondary, 
          lineHeight: '1.4',
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical'
        }}>
          {feature.description}
        </p>
      )}

      {/* Action Buttons */}
      <div style={{ 
        marginTop: 'auto',
        paddingTop: '0.75rem',
        borderTop: `1px solid ${theme.gridLine}`,
        display: 'flex', 
        gap: '0.5rem', 
        alignItems: 'center',
        flexWrap: 'nowrap'
      }}>
        <div 
          style={{ 
            display: 'flex', 
            gap: '0.5rem', 
            alignItems: 'center',
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.2s ease',
            pointerEvents: isHovered ? 'auto' : 'none'
          }}
        >
          {onEdit && (
            <button
              className="btn btn-secondary btn-icon"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(feature);
              }}
              style={{
                padding: '0.5rem',
                minWidth: '2.25rem',
                minHeight: '2.25rem',
                width: '2.25rem',
                height: '2.25rem',
                fontSize: '1rem',
                lineHeight: '1',
                flexShrink: 0
              }}
              title="Edit"
            >
              ✏️
            </button>
          )}
          <button
            className="btn btn-secondary btn-icon"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(feature.id);
            }}
            style={{
              padding: '0.5rem',
              minWidth: '2.25rem',
              minHeight: '2.25rem',
              width: '2.25rem',
              height: '2.25rem',
              fontSize: '1rem',
              color: theme.danger || '#dc2626',
              lineHeight: '1',
              flexShrink: 0
            }}
            title="Delete"
          >
            {Icons.delete}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeatureCard;
